import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '../../common/Button';
import FullBlockingModal from '../../common/FullBlockingModal';
import { supabase } from '../../../lib/supabase';
import { Profile } from '../../../types/profile';
import { toast } from 'react-toastify';

interface GiftCreditsModalProps {
  profile: Profile;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function GiftCreditsModal({ profile, onClose, onSuccess }: GiftCreditsModalProps) {
  const { t } = useTranslation(['admin', 'common']);
  const [amount, setAmount] = useState<number>(10);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (amount <= 0) {
      toast.error(
        t('creditGift.invalidAmount', { ns: 'admin', defaultValue: 'Amount must be positive' })
      );
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_gift_credits', {
        p_user_id: profile.id,
        p_amount: amount,
        p_reason: reason.trim() || null,
      });
      if (error) throw error;
      toast.success(
        t('creditGift.success', { ns: 'admin', defaultValue: 'Credits gifted successfully' })
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(
        (err as Error).message ||
          t('errors.generic', { ns: 'common', defaultValue: 'An error occurred' })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <FullBlockingModal>
      <div className="rounded-2xl overflow-hidden flex flex-col bg-white shadow-xl max-w-md w-full mx-4">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('creditGift.title', { ns: 'admin', defaultValue: 'Gift credits' })}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            type="button"
          >
            <X size={22} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            {t('creditGift.recipient', { ns: 'admin', defaultValue: 'Recipient' })}:{' '}
            <span className="font-medium text-gray-900">
              {profile.first_name} {profile.last_name}
            </span>{' '}
            ({t(`role.${profile.role}`, { ns: 'common' })})
          </p>
          <div>
            <label htmlFor="gift-amount" className="block text-sm font-medium text-gray-700 mb-1">
              {t('creditGift.amount', { ns: 'admin', defaultValue: 'Amount' })}
            </label>
            <input
              id="gift-amount"
              type="number"
              min={1}
              value={amount}
              onChange={e => setAmount(parseInt(e.target.value, 10) || 0)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label htmlFor="gift-reason" className="block text-sm font-medium text-gray-700 mb-1">
              {t('creditGift.reasonOptional', { ns: 'admin', defaultValue: 'Reason (optional)' })}
            </label>
            <textarea
              id="gift-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="transparent-with-border" onClick={onClose}>
              {t('common:form_actions.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving
                ? t('loading', { ns: 'common', defaultValue: 'Loading...' })
                : t('creditGift.submit', { ns: 'admin', defaultValue: 'Gift credits' })}
            </Button>
          </div>
        </form>
      </div>
    </FullBlockingModal>
  );
}
